import { useState, useEffect } from 'react'
import { useForm, Controller, type Resolver } from 'react-hook-form'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  Clock,
  Bot,
  MessageSquare,
  BellOff,
  CheckCircle2,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
} from 'lucide-react'
import { businessHoursApi, type WeeklySchedule } from '@/services/businessHoursApi'

const dayScheduleSchema = z.object({
  enabled: z.boolean(),
  startTime: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/).optional(),
  endTime: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/).optional(),
})

const schema = z.object({
  businessHoursEnabled: z.boolean(),
  timezone: z.string().min(1),
  weeklySchedule: z.object({
    monday: dayScheduleSchema,
    tuesday: dayScheduleSchema,
    wednesday: dayScheduleSchema,
    thursday: dayScheduleSchema,
    friday: dayScheduleSchema,
    saturday: dayScheduleSchema,
    sunday: dayScheduleSchema,
  }),
  offHoursBehavior: z.enum(['respond_with_offline_message', 'use_ai_with_notice', 'silence']),
  offHoursMessage: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

const DAYS = [
  { key: 'monday', label: 'Lun', fullLabel: 'Lunes' },
  { key: 'tuesday', label: 'Mar', fullLabel: 'Martes' },
  { key: 'wednesday', label: 'Mié', fullLabel: 'Miércoles' },
  { key: 'thursday', label: 'Jue', fullLabel: 'Jueves' },
  { key: 'friday', label: 'Vie', fullLabel: 'Viernes' },
  { key: 'saturday', label: 'Sáb', fullLabel: 'Sábado' },
  { key: 'sunday', label: 'Dom', fullLabel: 'Domingo' },
] as const

const TIMEZONES = [
  { value: 'America/Caracas', label: 'UTC-04:00 Caracas (Venezuela)' },
  { value: 'America/Bogota', label: 'UTC-05:00 Bogotá' },
  { value: 'America/Lima', label: 'UTC-05:00 Lima' },
  { value: 'America/New_York', label: 'UTC-05:00 Eastern Time (US & Canada)' },
  { value: 'America/Mexico_City', label: 'UTC-06:00 Ciudad de México' },
  { value: 'America/Santiago', label: 'UTC-03:00 Santiago' },
  { value: 'America/Buenos_Aires', label: 'UTC-03:00 Buenos Aires' },
  { value: 'America/Los_Angeles', label: 'UTC-08:00 Los Ángeles' },
  { value: 'Europe/Madrid', label: 'UTC+01:00 Madrid' },
  { value: 'Europe/London', label: 'UTC+00:00 Londres' },
  { value: 'UTC', label: 'UTC+00:00 UTC' },
]

const OFF_HOURS_OPTIONS: ReadonlyArray<{
  value: FormValues['offHoursBehavior']
  label: string
  badge: string
  description: string
  footerHint: string
  icon: typeof Bot
}> = [
  {
    value: 'use_ai_with_notice',
    label: 'Atender con el Bot (24/7)',
    badge: 'Recomendado para ventas',
    description:
      'El asistente responde consultas, cotiza productos y toma pedidos automáticamente sin enviar mensajes de fuera de horario.',
    footerHint: '✓ Atiende y vende 24/7 sin interrupciones',
    icon: Bot,
  },
  {
    value: 'respond_with_offline_message',
    label: 'Mensaje fuera de horario',
    badge: 'Pausa el bot',
    description:
      'Detiene al asistente fuera de tu horario laboral y envía únicamente tu mensaje personalizado de tienda cerrada.',
    footerHint: 'Envía aviso de horario y espera al equipo',
    icon: MessageSquare,
  },
  {
    value: 'silence',
    label: 'Dejar en silencio (No responder)',
    badge: 'Sin respuesta automática',
    description:
      'No responde automáticamente con el bot ni envía avisos fuera de horario. Los mensajes quedan en bandeja para atención manual.',
    footerHint: 'Silencio total fuera de horario laboral',
    icon: BellOff,
  },
]

const DEFAULT_OFF_HOURS_MESSAGE =
  '¡Hola! Gracias por comunicarte con nosotros. Actualmente estamos fuera de nuestro horario de atención. Déjanos tu consulta y te responderemos a primera hora.'

const DEFAULT_VALUES: FormValues = {
  businessHoursEnabled: true,
  timezone: 'America/Caracas',
  weeklySchedule: {
    monday: { enabled: true, startTime: '09:00', endTime: '18:00' },
    tuesday: { enabled: true, startTime: '09:00', endTime: '18:00' },
    wednesday: { enabled: true, startTime: '09:00', endTime: '18:00' },
    thursday: { enabled: true, startTime: '09:00', endTime: '18:00' },
    friday: { enabled: true, startTime: '09:00', endTime: '18:00' },
    saturday: { enabled: false, startTime: '09:00', endTime: '18:00' },
    sunday: { enabled: false, startTime: '09:00', endTime: '18:00' },
  },
  offHoursBehavior: 'use_ai_with_notice',
  offHoursMessage: DEFAULT_OFF_HOURS_MESSAGE,
}

function fmt12(time?: string) {
  if (!time) return '--:--'
  const [hStr, mStr] = time.split(':')
  const h = parseInt(hStr, 10)
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${String(h12).padStart(2, '0')}:${mStr} ${suffix}`
}

const formResolver: Resolver<FormValues> = async (values) => {
  const parsed = schema.safeParse(values)
  if (parsed.success) {
    return { values: parsed.data, errors: {} }
  }
  return { values: {}, errors: {} }
}

interface AdvancedSettingsProps {
  className?: string
  initialData?: Partial<FormValues>
  onSave?: (values: FormValues) => Promise<void>
}

export default function AdvancedSettings({ className, initialData, onSave }: AdvancedSettingsProps) {
  const [initialLoading, setInitialLoading] = useState(!initialData)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [editHours, setEditHours] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: formResolver,
    defaultValues: {
      ...DEFAULT_VALUES,
      ...initialData,
    },
  })

  useEffect(() => {
    if (initialData) return
    let mounted = true
    businessHoursApi
      .getBusinessHours()
      .then((data) => {
        if (!mounted) return
        reset({
          businessHoursEnabled: data.businessHoursEnabled,
          timezone: data.timezone || DEFAULT_VALUES.timezone,
          weeklySchedule: data.weeklySchedule || DEFAULT_VALUES.weeklySchedule,
          offHoursBehavior: data.offHoursBehavior || DEFAULT_VALUES.offHoursBehavior,
          offHoursMessage: data.offHoursMessage || DEFAULT_OFF_HOURS_MESSAGE,
        })
      })
      .catch(() => {
        if (!mounted) return
        reset(DEFAULT_VALUES)
      })
      .finally(() => {
        if (mounted) setInitialLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [initialData, reset])

  const businessHoursEnabled = watch('businessHoursEnabled')
  const weeklySchedule = watch('weeklySchedule')
  const offHoursBehavior = watch('offHoursBehavior')
  const offHoursMessage = watch('offHoursMessage') || DEFAULT_OFF_HOURS_MESSAGE

  const firstActiveDay = DAYS.find((d) => weeklySchedule[d.key]?.enabled)
  const rangeStart = firstActiveDay ? weeklySchedule[firstActiveDay.key]?.startTime : '09:00'
  const rangeEnd = firstActiveDay ? weeklySchedule[firstActiveDay.key]?.endTime : '18:00'

  const applyCommonHoursToAllEnabledDays = (startTime: string, endTime: string) => {
    for (const day of DAYS) {
      if (weeklySchedule[day.key]?.enabled) {
        setValue(`weeklySchedule.${day.key}.startTime`, startTime, { shouldDirty: true })
        setValue(`weeklySchedule.${day.key}.endTime`, endTime, { shouldDirty: true })
      }
    }
  }

  const onSubmit = async (values: FormValues) => {
    try {
      setSaving(true)
      const ws: WeeklySchedule = {
        monday: {
          enabled: values.weeklySchedule.monday.enabled,
          startTime: values.weeklySchedule.monday.startTime || '09:00',
          endTime: values.weeklySchedule.monday.endTime || '18:00',
        },
        tuesday: {
          enabled: values.weeklySchedule.tuesday.enabled,
          startTime: values.weeklySchedule.tuesday.startTime || '09:00',
          endTime: values.weeklySchedule.tuesday.endTime || '18:00',
        },
        wednesday: {
          enabled: values.weeklySchedule.wednesday.enabled,
          startTime: values.weeklySchedule.wednesday.startTime || '09:00',
          endTime: values.weeklySchedule.wednesday.endTime || '18:00',
        },
        thursday: {
          enabled: values.weeklySchedule.thursday.enabled,
          startTime: values.weeklySchedule.thursday.startTime || '09:00',
          endTime: values.weeklySchedule.thursday.endTime || '18:00',
        },
        friday: {
          enabled: values.weeklySchedule.friday.enabled,
          startTime: values.weeklySchedule.friday.startTime || '09:00',
          endTime: values.weeklySchedule.friday.endTime || '18:00',
        },
        saturday: {
          enabled: values.weeklySchedule.saturday.enabled,
          startTime: values.weeklySchedule.saturday.startTime || '09:00',
          endTime: values.weeklySchedule.saturday.endTime || '18:00',
        },
        sunday: {
          enabled: values.weeklySchedule.sunday.enabled,
          startTime: values.weeklySchedule.sunday.startTime || '09:00',
          endTime: values.weeklySchedule.sunday.endTime || '18:00',
        },
      }

      await businessHoursApi.updateBusinessHours({
        businessHoursEnabled: values.businessHoursEnabled,
        timezone: values.timezone,
        weeklySchedule: ws,
        offHoursBehavior: values.offHoursBehavior,
        offHoursMessage: values.offHoursMessage,
      })
      if (onSave) await onSave(values)
      setLastSaved(new Date())
      toast.success('Configuración de horarios guardada')
    } catch {
      toast.error('Error al guardar la configuración')
    } finally {
      setSaving(false)
    }
  }

  if (initialLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-8 h-8 animate-spin text-[#075E54]" />
      </div>
    )
  }

  const activeOption = OFF_HOURS_OPTIONS.find((opt) => opt.value === offHoursBehavior)

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn('space-y-6 p-4 sm:p-6', className)}>
      {/* Banner explicativo */}
      <div className="p-4 rounded-2xl border border-emerald-200/80 bg-gradient-to-r from-emerald-50/80 to-teal-50/40">
        <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#075E54] flex items-center justify-center flex-shrink-0 shadow-sm">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                Control de Horario Laboral y Respuesta Automática
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Define el horario de tu equipo humano y elige si fuera de turno deseas que el bot atienda 24/7,
                envíe un mensaje de tienda cerrada o permanezca en silencio sin responder automáticamente.
              </p>
            </div>
          </div>

          <Controller
            name="businessHoursEnabled"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2.5 bg-white/90 px-3.5 py-2 rounded-xl border border-emerald-200/70 shadow-sm flex-shrink-0">
                <span className="text-xs font-semibold text-gray-700">
                  {field.value ? 'Horario activo' : 'Horario desactivado'}
                </span>
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  aria-label="Activar control de horarios"
                />
              </div>
            )}
          />
        </div>
      </div>

      {/* Tarjetas de comportamiento fuera de horario (3 columnas en escritorio) */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-gray-900">
              ¿Qué debe hacer el bot fuera de tu horario de atención?
            </h4>
            <p className="text-xs text-gray-500">
              Selecciona cómo responder cuando un cliente escriba fuera de las horas laborales configuradas.
            </p>
          </div>
          {activeOption && (
            <span className="hidden sm:inline-flex rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold">
              Modo actual: {activeOption.label}
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {OFF_HOURS_OPTIONS.map((opt) => {
            const isSelected = offHoursBehavior === opt.value
            const Icon = opt.icon

            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setValue('offHoursBehavior', opt.value, { shouldDirty: true })
                  if (!businessHoursEnabled && opt.value !== 'use_ai_with_notice') {
                    setValue('businessHoursEnabled', true, { shouldDirty: true })
                  }
                }}
                className={cn(
                  'text-left rounded-2xl border-2 p-4 transition-all duration-200 shadow-sm flex flex-col justify-between',
                  isSelected
                    ? 'border-[#075E54] bg-emerald-50/40 ring-1 ring-[#075E54] shadow-md'
                    : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow'
                )}
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                          isSelected ? 'bg-[#075E54] text-white' : 'bg-slate-100 text-slate-600'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200/70 rounded-md px-1.5 py-0.5 mb-0.5">
                          {opt.badge}
                        </span>
                        <h5 className="text-sm font-bold text-gray-900 leading-snug">{opt.label}</h5>
                      </div>
                    </div>
                    {isSelected ? (
                      <CheckCircle2 className="w-5 h-5 text-[#075E54] flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border border-gray-300 flex-shrink-0" />
                    )}
                  </div>

                  <p className="mt-2.5 text-xs text-gray-600 leading-relaxed">{opt.description}</p>
                </div>

                <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-1.5 text-[11px] font-medium text-emerald-800">
                  <span>{isSelected ? opt.footerHint : 'Pulsa para seleccionar'}</span>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* Detalle dinámico según el modo seleccionado */}
      {offHoursBehavior === 'use_ai_with_notice' && (
        <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200/80 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm text-emerald-950">
            <p className="font-bold mb-0.5">Atención continua con el Bot (Sin mensaje de tienda cerrada)</p>
            <p className="text-emerald-800 text-xs leading-relaxed">
              El asistente responderá con normalidad a cualquier hora, mostrando productos, precios y tomando pedidos
              <strong> sin enviar el aviso de &ldquo;estamos fuera de horario&rdquo;</strong>. Solo mencionará el horario de oficina si el cliente solicita hablar con un asesor humano.
            </p>
          </div>
        </div>
      )}

      {offHoursBehavior === 'silence' && (
        <div className="p-4 rounded-2xl bg-slate-100/80 border border-slate-200 flex items-start gap-3">
          <BellOff className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm text-slate-900">
            <p className="font-bold mb-0.5">Silencio automático activado fuera de horario</p>
            <p className="text-slate-600 text-xs leading-relaxed">
              Fuera de los días y horas configurados abajo, el bot <strong>no responderá automáticamente</strong> ni enviará mensajes de ausencia. Los mensajes entrantes quedarán registrados en la bandeja de entrada para que tu equipo responda manualmente.
            </p>
          </div>
        </div>
      )}

      {offHoursBehavior === 'respond_with_offline_message' && (
        <div className="grid gap-4 lg:grid-cols-2 rounded-2xl border border-gray-200 bg-slate-50/60 p-4">
          <div className="space-y-1.5">
            <label htmlFor="offHoursMessage" className="block text-sm font-bold text-gray-900">
              Mensaje personalizado fuera de horario
            </label>
            <p className="text-xs text-gray-500">
              Este único mensaje se enviará automáticamente cuando un cliente escriba fuera del horario laboral.
            </p>
            <textarea
              id="offHoursMessage"
              {...register('offHoursMessage')}
              rows={4}
              maxLength={250}
              className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none transition"
              placeholder="Escribe el mensaje que recibirán los clientes cuando te contacten fuera de horario..."
            />
            <p className="text-[11px] text-gray-400">
              Máximo 250 caracteres.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
              <MessageSquare className="w-4 h-4 text-[#075E54]" />
              <span>Vista previa en WhatsApp</span>
            </div>
            <div className="bg-[#E5DDD5]/40 p-4 rounded-2xl border border-gray-200">
              <div className="flex justify-start">
                <div className="bg-white text-gray-900 text-xs sm:text-sm rounded-2xl rounded-tl-none px-3.5 py-2.5 shadow-sm max-w-[92%] border border-gray-100">
                  <p className="whitespace-pre-wrap">{offHoursMessage}</p>
                  <span className="text-[10px] text-gray-400 block text-right mt-1.5">11:24 PM ✓✓</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuración de Zona Horaria, Días y Rango de Horas */}
      {businessHoursEnabled && (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 space-y-5">
          <div className="grid gap-5 lg:grid-cols-2">
            {/* Zona Horaria */}
            <div>
              <label htmlFor="timezone-select" className="block text-sm font-bold text-gray-900 mb-1">
                Zona horaria del negocio
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Se utiliza para calcular la hora local exacta de apertura y cierre.
              </p>
              <select
                id="timezone-select"
                {...register('timezone')}
                className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-gray-900 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none transition"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Días de atención semanal */}
            <div>
              <span className="block text-sm font-bold text-gray-900 mb-1">
                Días laborales de atención
              </span>
              <p className="text-xs text-gray-500 mb-2">
                Marca los días en que tu equipo atiende en horario regular.
              </p>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day) => {
                  const isActive = weeklySchedule[day.key]?.enabled
                  return (
                    <Controller
                      key={day.key}
                      name={`weeklySchedule.${day.key}.enabled`}
                      control={control}
                      render={({ field }) => (
                        <button
                          type="button"
                          onClick={() => field.onChange(!field.value)}
                          className={cn(
                            'flex h-9 items-center justify-center gap-1.5 rounded-xl px-3 text-xs font-semibold transition-all',
                            isActive
                              ? 'bg-[#075E54] text-white shadow-sm'
                              : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200/70'
                          )}
                        >
                          {isActive && <Check className="w-3.5 h-3.5" />}
                          {day.label}
                        </button>
                      )}
                    />
                  )
                })}
              </div>
            </div>
          </div>

          {/* Resumen de horas y desplegable por día */}
          <div className="pt-3 border-t border-gray-100 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 px-4 py-3 rounded-xl border border-slate-200/80">
              <div className="flex items-center gap-2.5">
                <Clock className="w-4 h-4 text-[#075E54]" />
                <span className="text-xs sm:text-sm font-semibold text-gray-800">
                  Horario habitual: {fmt12(rangeStart)} – {fmt12(rangeEnd)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditHours((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#075E54] hover:text-[#064E46] bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm transition"
                >
                  <span>{editHours ? 'Ocultar detalle por día' : 'Ajustar horas por día'}</span>
                  {editHours ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {editHours && (
              <div className="space-y-3 pt-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-gray-500">
                    Configura la hora de apertura y cierre para cada día activo:
                  </p>
                  {firstActiveDay && (
                    <button
                      type="button"
                      onClick={() =>
                        applyCommonHoursToAllEnabledDays(rangeStart || '09:00', rangeEnd || '18:00')
                      }
                      className="text-xs font-medium text-[#075E54] hover:underline"
                    >
                      Aplicar {fmt12(rangeStart)} – {fmt12(rangeEnd)} a todos los días activos
                    </button>
                  )}
                </div>

                <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                  {DAYS.filter((d) => weeklySchedule[d.key]?.enabled).map((day) => (
                    <div
                      key={day.key}
                      className="flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50/80 rounded-xl border border-gray-200"
                    >
                      <span className="text-xs font-semibold text-gray-700 w-16 truncate">
                        {day.fullLabel}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="time"
                          aria-label={`Hora de inicio ${day.fullLabel}`}
                          {...register(`weeklySchedule.${day.key}.startTime`)}
                          className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54]"
                        />
                        <span className="text-gray-400 text-xs">–</span>
                        <input
                          type="time"
                          aria-label={`Hora de cierre ${day.fullLabel}`}
                          {...register(`weeklySchedule.${day.key}.endTime`)}
                          className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54]"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer de guardado alineado a la derecha (sin botón gigante fijo) */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-100">
        <div>
          {lastSaved && (
            <p className="text-xs text-gray-400">
              Última actualización a las{' '}
              {lastSaved.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting || saving}
          className="w-full sm:w-auto bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl px-6 py-2.5 font-medium shadow-sm"
        >
          {saving || isSubmitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Guardando...
            </span>
          ) : (
            'Guardar configuración'
          )}
        </Button>
      </div>
    </form>
  )
}
